import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider } from '@mantine/core';
import AppText from '../../common/AppText';
import AppStack from '../../common/AppStack';
import ConfirmModal from '../../common/ConfirmModal';
import AppCheckbox from '../../common/AppCheckbox';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';

const ExternalProductSyncModal = ({
  isOpen,
  onClose,
  loading,
  onSubmit,
  hasUnsavedChanges,
  configs
}) => {
  const [formState, setFormState] = useState({ syncOptions: [] });
  const syncOptions = configs[0]?.products?.syncOptions || [];

  useEffect(() => {
    if (isOpen) {
      setFormState({ syncOptions: [] });
    }
  }, [isOpen]);

  return (
    configs && (
      <ConfirmModal
        cancelActionText={hasUnsavedChanges ? 'Close' : 'Cancel'}
        confirmActionText="Yes, sync product"
        formSectionProps={{ isSubmitHidden: hasUnsavedChanges }}
        isLoading={loading}
        isOpen={isOpen}
        message={
          hasUnsavedChanges ? (
            <AppStack style={{ gap: 10 }}>
              <AppText>
                You have unsaved changes. Please save or discard them before
                syncing this product.
              </AppText>
            </AppStack>
          ) : (
            <AppStack style={{ gap: 8 }}>
              <AppText style={{ fontSize: 14 }}>
                Are you sure you want to sync this product product using the
                latest data from external providers?
              </AppText>
              <AppText
                style={{
                  fontSize: 14,
                  color: '#666'
                }}
              >
                This will overwrite the current version in our system for all
                related products to this external connection.
              </AppText>

              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                This action cannot be undone.
              </AppText>

              <AppText
                style={{
                  fontSize: 14,
                  color: '#666',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}
              >
                No changes will be made to external data sources.
              </AppText>

              {syncOptions.length > 0 && (
                <AppStack style={{ gap: 16, marginTop: 8 }}>
                  <Divider />
                  <AppStack style={{ gap: 5 }}>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      Extra sync Options:
                    </AppText>
                    <AppText
                      style={{
                        fontSize: 14,
                        color: '#666',
                        fontStyle: 'italic'
                      }}
                    >
                      Select the options you want to sync:
                    </AppText>
                  </AppStack>
                  <AppCard
                    radius="md"
                    shadow="none"
                    style={{ padding: 0 }}
                    withBorder
                  >
                    <AppStack style={{ gap: 0 }}>
                      {syncOptions.map((option, index) => {
                        const selected = formState.syncOptions.includes(
                          option.dataValue
                        );

                        return (
                          <React.Fragment key={option.dataValue}>
                            {index > 0 && <Divider />}
                            <AppFlexbox
                              onClick={() => {
                                setFormState({
                                  ...formState,
                                  syncOptions: selected
                                    ? formState.syncOptions.filter(
                                        (s) => s !== option.dataValue
                                      )
                                    : [
                                        ...formState.syncOptions,
                                        option.dataValue
                                      ]
                                });
                              }}
                              style={{ padding: 16, cursor: 'pointer' }}
                            >
                              <AppCheckbox
                                key={option.dataValue}
                                checked={selected}
                                description={option.description}
                                disabled={loading}
                                label={option.label}
                                onChange={() => {}}
                                style={{
                                  fontWeight: 500,
                                  pointerEvents: 'none'
                                }}
                              />
                            </AppFlexbox>
                          </React.Fragment>
                        );
                      })}
                    </AppStack>
                  </AppCard>
                </AppStack>
              )}
            </AppStack>
          )
        }
        onCancel={onClose}
        onConfirm={() => {
          onSubmit(formState.syncOptions);
        }}
        title="Manually sync external product?"
      />
    )
  );
};

ExternalProductSyncModal.propTypes = {
  configs: PropTypes.array,
  hasUnsavedChanges: PropTypes.bool,
  isOpen: PropTypes.bool,
  loading: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func
};

export default ExternalProductSyncModal;
